import {
    createApp,
    watch,
} from 'vue';
import { init, setTag, vueRouterInstrumentation, setUser } from '@sentry/vue';
import { BrowserTracing } from '@sentry/tracing';
import { createGtm } from '@gtm-support/vue-gtm';
import {
    createPinia,
    storeToRefs,
} from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import './assets/scss/tailwind-preflight.css';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import relativeTime from 'dayjs/plugin/relativeTime';
import VueSplide from '@splidejs/vue-splide';
import { makeGA4Tracker } from './utils/tracker-ga4';
import { registerTracker, trackVisibility } from './utils/tracking';
import App from './components/App.vue';
import config from './config';
import router from './router';
import { hasSlotContent } from './utils/has-slot-content';
import './assets/scss/main.scss';
import {
    useAuth,
} from './stores/auth-store';
import 'dayjs/locale/de';

/* day.js setup */
dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(isSameOrBefore);
dayjs.locale('de-at'); // use locale globally

const Sentry = {
    init,
    setTag,
    vueRouterInstrumentation,
    setUser,
};

const app = createApp(App);
const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

app.use(pinia);
app.use(router);
app.use(VueSplide);

app.config.globalProperties.$hasSlotContent = hasSlotContent; // slot content check until an official way is released

if (config.sentry.active) {
    Sentry.init({
        app,
        dsn: config.sentry.dsn,
        release: config.release,
        environment: config.environment,
        allowUrls: [
            /.*\.karriere\.at/,
            /kcdn\.at/,
        ],
        integrations: [
            new BrowserTracing({
                routingInstrumentation: Sentry.vueRouterInstrumentation(router),
                tracingOrigins: [
                    'localhost',
                    'kcdn.at',
                    // This matches all requests relative to the current domain.
                    // E.g., `/backend/stuff`.
                    /^\//,
                    // Match all karriere.at origins except `gtm.karriere.at`.
                    // If you want to exclude other subdomains you can do it
                    // with a regular expression like this:
                    // `/^((?!gtm\.|other\.).)*karriere.at.*$/`
                    /^((?!gtm\.|login\.).)*karriere.at.*$/,
                ],
            }),
        ],
        tracesSampleRate: config.sentry.tracesSampleRate,
        trackComponents: true,
        hooks: ['activate', 'create', 'destroy', 'mount', 'update'],
    });

    Sentry.setTag('version', config.version);
    Sentry.setTag('source', 'js');

    const auth = useAuth();
    const { uuid } = storeToRefs(auth);
    watch(uuid, () => {
        Sentry.setUser({ id: uuid.value });
    });
}

if (config.gtm.active) {
    app.use(createGtm({
        id: config.gtm.id,
        enabled: false,
        loadScript: true,
        // Set debug to true if you want to see the funnel events (every trackEvent call).
        debug: false,
    }));
    registerTracker(makeGA4Tracker());
}

app.directive('track-visibility', {
    mounted(el, { value = { element: null, elementDetail: null } }) {
        if (value?.element) {
            trackVisibility(value);
        } else {
            trackVisibility({
                element: el.dataset.gtmElement ?? null,
                elementDetail: el.dataset.gtmElementDetail ?? null,
            });
        }
    },
});

app.mount('#app');
